import React, { useMemo } from 'react';
import { css, ThemeProvider } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

type ListProps = {
  direction: 'horizontal' | 'vertical',
  children: React.Node,
};

type ItemProps = {
  dataSeparator?: string,
  margin?: 'none',
  tiny?: boolean,
  children: React.Node,
};

export const SeparatedListWrapper = ({ direction, children }: ListProps) => {
  const themeValue = useMemo(() => ({ direction }), [direction]);
  return (
    <ThemeProvider theme={themeValue}>
      <List direction={direction}>{children}</List>
    </ThemeProvider>
  );
};

export const SeparatedItemWrapper = ({ dataSeparator = 'Or', margin, tiny, children }: ItemProps) => {
  const { t } = useTranslation();
  const separatorText = dataSeparator === 'Or' ? t('auth.signIn.form:or') : dataSeparator;
  return (
    <ListItem>
      {separatorText && <ListItemSeparator tiny={tiny} margin={margin} aria-hidden="true">{separatorText}</ListItemSeparator>}
      {children}
    </ListItem>
  );
};

const List = styled('ul')`
  list-style-type: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: ${({ direction }) => (direction === 'horizontal' ? 'row' : 'column')};
`;

const separatorLine = ({ theme, tiny }) => {
  const leftLine = tiny ? '46%' : '40%';
  const rightLine = tiny ? '54%' : '60%';

  return css`
    background: linear-gradient(
      ${theme.direction === 'horizontal' ? 'to bottom' : 'to right'},
      ${theme.color.lightGrey} ${leftLine},
      transparent ${leftLine},
      transparent ${rightLine},
      ${theme.color.lightGrey} ${rightLine}
    );
  `;
};


const ListItemSeparator = styled('span')`
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: capitalize;
  position: absolute;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ tiny, theme }) => tiny && theme.color.grey};
  ${separatorLine};
  ${({ theme }) =>
  theme.direction === 'horizontal'
    ? `
      top: 0;
      width: 1px;
      height: 100%;
      `
    : `
      left: 0;
      width: 100%;
      height: 1px;
    `};
    ${({ theme, margin }) => margin !== 'none' && (theme.direction === 'horizontal' ? 'right: -14px' : 'bottom: -14px')};
    @media (${({ theme }) => theme.breakpoint.desktop}) {
      font-size: ${({ tiny, theme }) => !tiny && theme.fontSize.xl};
      ${({ theme, margin }) =>
  margin !== 'none' && (theme.direction === 'horizontal' ? 'right: -44px' : 'bottom: -44px')};
    }
`;


const ListItem = styled('li')`
  flex: 1;
  margin: ${({ theme, margin }) => margin !== 'none' && (theme.direction === 'horizontal' ? '0 14px' : '14px 0')};
  position: relative;
  @media (${({ theme }) => theme.breakpoint.desktop}) {
    margin: ${({ theme, margin }) => margin !== 'none' && (theme.direction === 'horizontal' ? '0 44px' : '44px 0')};
  }

  &:first-of-type {
    ${({ theme }) => (theme.direction === 'horizontal' ? 'margin-left: 0' : 'margin-top: 0')};
  }
  &:last-of-type {
    ${({ theme }) => (theme.direction === 'horizontal' ? 'margin-right: 0' : 'margin-bottom: 0')};
    ${ListItemSeparator} {
      display: none;
    }
  }
`;

SeparatedListWrapper.defaultProps = {
  direction: 'horizontal',
};

export const SeparatedList = {
  List: SeparatedListWrapper,
  Item: SeparatedItemWrapper,
};
